import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type ToastTone = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  message?: string;
}

interface ToastContextValue {
  notify: (toast: { tone?: ToastTone; title: string; message?: string }) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const TONE_STYLES: Record<ToastTone, { wrap: string; icon: string; iconWrap: string }> = {
  success: {
    wrap: 'border-primary-200 bg-primary-50',
    icon: 'ri-checkbox-circle-line',
    iconWrap: 'bg-primary-100 text-primary-700',
  },
  error: {
    wrap: 'border-red-200 bg-red-50',
    icon: 'ri-error-warning-line',
    iconWrap: 'bg-red-100 text-red-600',
  },
  warning: {
    wrap: 'border-amber-200 bg-amber-50',
    icon: 'ri-alert-line',
    iconWrap: 'bg-amber-100 text-amber-700',
  },
  info: {
    wrap: 'border-accent-200 bg-accent-50',
    icon: 'ri-information-line',
    iconWrap: 'bg-accent-100 text-accent-700',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    ({ tone = 'info', title, message }: { tone?: ToastTone; title: string; message?: string }) => {
      const id = Date.now() + Math.floor(Math.random() * 1000);
      setToasts((prev) => [...prev, { id, tone, title, message }]);
      window.setTimeout(() => remove(id), 4200);
    },
    [remove],
  );

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[70] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-2">
        {toasts.map((toast) => {
          const style = TONE_STYLES[toast.tone];
          return (
            <div
              key={toast.id}
              className={[
                'pointer-events-auto flex items-start gap-3 rounded-lg border p-3 animate-toast-in',
                style.wrap,
              ].join(' ')}
            >
              <span
                className={[
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                  style.iconWrap,
                ].join(' ')}
              >
                <i className={`${style.icon} text-lg`}></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground-900">{toast.title}</p>
                {toast.message && (
                  <p className="mt-0.5 text-xs text-foreground-600">{toast.message}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => remove(toast.id)}
                aria-label="Cerrar notificación"
                className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast debe usarse dentro de ToastProvider');
  }
  return context;
}